import addEmojisToText from 'helpers/addEmojisToText'
import { useSwitchState } from 'hooks/useSwitchState'
import { Post, UserFull } from '@wellesley/openapi'
import { useAppsNavigate } from '@wellesley/router'
import { FC, MouseEvent, useMemo, useRef } from 'react'
import {
  truncateHtml,
  OPEN_BUTTON_TEXT,
  REDIRECT_BUTTON_TEXT,
} from 'helpers/truncate'

import useTranslation from '../hooks/useTranslation'
import TranslationControls from 'components/TranslationControls'
import { getFeedPostRoute } from '../../helpers/getFeedPostRoute'
import HoverMentionPopup from './HoverMentionPopup'
import { MarkdownHtmlPreview } from '../../../../markdown'

type TFeedTextSection = {
  feed: Post
  showFullText?: boolean
  redirectOnShowMore?: boolean
  groupName?: string
  channel?: Pick<UserFull, 'groupUserName' | 'id'>
  containerWidth?: number
}

const FeedTextSection: FC<TFeedTextSection> = ({
  feed,
  showFullText,
  redirectOnShowMore,
  groupName,
  channel,
  containerWidth,
}) => {
  const {
    isLoading,
    canTranslate,
    translation,
    isOriginalText,
    makeTranslate,
    showOriginalText,
  } = useTranslation(feed)

  const navigate = useAppsNavigate()

  const [isExpanded, , , toggleExpanded] = useSwitchState(false)
  const textInFeed = isOriginalText ? feed.text : translation

  const textRef = useRef<HTMLDivElement>(null)
  const textContainerWidth = containerWidth || textRef.current?.clientWidth

  const truncatedText = useMemo(
    () =>
      showFullText
        ? textInFeed
        : truncateHtml({
            html: textInFeed,
            maxLines: 15,
            isExpanded,
            containerWidth: textContainerWidth,
            openText: redirectOnShowMore
              ? REDIRECT_BUTTON_TEXT
              : OPEN_BUTTON_TEXT,
          }),
    [textInFeed, isExpanded, textContainerWidth],
  )

  const handleShowMore = (evt: MouseEvent<HTMLDivElement>) => {
    const target = evt.target
    if (
      target instanceof HTMLElement &&
      target.classList.contains('toggle-text')
    ) {
      evt.preventDefault()
      evt.stopPropagation()

      if (redirectOnShowMore) {
        const postRoute = getFeedPostRoute({ feed, groupName, channel })
        navigate(postRoute)
      } else {
        toggleExpanded()
      }
    }
  }

  const htmlTextWithEmojis = useMemo(
    () => addEmojisToText(truncatedText, feed.emojis),
    [truncatedText, feed.emojis],
  )

  return (
    <div data-testid="feed-text" onClick={handleShowMore}>
      <HoverMentionPopup>
        <MarkdownHtmlPreview
          className="mt-m post"
          htmlString={htmlTextWithEmojis}
        />
      </HoverMentionPopup>

      {canTranslate && (
        <TranslationControls
          className="mt-s"
          isShowingTranslation={!isOriginalText}
          isLoading={isLoading}
          onTranslate={makeTranslate}
          onShowOriginal={showOriginalText}
        />
      )}
    </div>
  )
}

export default FeedTextSection
